// 交易所初筛问卷主要类型定义
// 基于 docs/问卷/交易所初筛问卷设计.md

// 导入并重新导出枚举类型
import {
  EnterpriseType,
  RegistrationType,
  CreditRating,
  InternationalRating,
  AssetScale,
  Industry,
  DebtRatio,
  ProfitScale,
  CashFlowStatus,
  DefaultRecord,
  GreenProjectStatus,
  GreenProjectCategory,
  GreenStandardCompliance,
  QuestionnaireStatus,
  ScreeningDecision,
} from './questionnaire-enums';

export {
  EnterpriseType,
  RegistrationType,
  CreditRating,
  InternationalRating,
  AssetScale,
  Industry,
  DebtRatio,
  ProfitScale,
  CashFlowStatus,
  DefaultRecord,
  GreenProjectStatus,
  GreenProjectCategory,
  GreenStandardCompliance,
  QuestionnaireStatus,
  ScreeningDecision,
};

// 补充的枚举类型
export enum InvestmentScale {
  ABOVE_10B = 'above_10b', // 100亿元以上
  FROM_5B_TO_10B = '5b_to_10b', // 50-100亿元
  FROM_2B_TO_5B = '2b_to_5b', // 20-50亿元
  FROM_1B_TO_2B = '1b_to_2b', // 10-20亿元
  FROM_500M_TO_1B = '500m_to_1b', // 5-10亿元
  BELOW_500M = 'below_500m', // 5亿元以下
}

export enum IssuanceScale {
  ABOVE_5B = 'above_5b', // 50亿元以上
  FROM_2B_TO_5B = '2b_to_5b', // 20-50亿元
  FROM_1B_TO_2B = '1b_to_2b', // 10-20亿元
  FROM_500M_TO_1B = '500m_to_1b', // 5-10亿元
  FROM_300M_TO_500M = '300m_to_500m', // 3-5亿元
  BELOW_300M = 'below_300m', // 3亿元以下
}

export enum IssuanceTime {
  WITHIN_1_MONTH = 'within_1_month', // 1个月内
  WITHIN_3_MONTHS = 'within_3_months', // 3个月内
  WITHIN_6_MONTHS = 'within_6_months', // 6个月内
  WITHIN_1_YEAR = 'within_1_year', // 1年内
  ABOVE_1_YEAR = 'above_1_year', // 1年以上
}

export enum ProductType {
  PUBLIC_GREEN_BOND = 'public_green_bond', // 公开发行绿色公司债
  PRIVATE_GREEN_BOND = 'private_green_bond', // 私募绿色债券
  GREEN_MTN = 'green_mtn', // 绿色中期票据
  GREEN_SYNDICATED_LOAN = 'green_syndicated_loan', // 绿色银团贷款
  GREEN_ABS_REITS = 'green_abs_reits', // 绿色ABS/REITs
  OTHER_GREEN_PRODUCT = 'other_green_product', // 其他绿色融资产品
}

export enum TargetMarket {
  DOMESTIC = 'domestic', // 境内市场
  OFFSHORE = 'offshore', // 离岸市场
  MACAU = 'macau', // 澳门市场
  FLEXIBLE = 'flexible', // 根据条件灵活选择
}

export enum LitigationStatus {
  NO_MAJOR_LITIGATION = 'no_major_litigation', // 无重大诉讼
  MINOR_LITIGATION = 'minor_litigation', // 有诉讼但金额较小
  MAJOR_LITIGATION_HIGH_WIN_RATE = 'major_litigation_high_win_rate', // 重大诉讼胜诉概率高
  MAJOR_LITIGATION_HIGH_LOSS_RISK = 'major_litigation_high_loss_risk', // 存在重大败诉风险
}

export enum EnvironmentalCompliance {
  GOOD_COMPLIANCE = 'good_compliance', // 环保合规良好
  MINOR_ISSUES_RESOLVED = 'minor_issues_resolved', // 轻微问题已整改
  VIOLATIONS_BEING_HANDLED = 'violations_being_handled', // 违规正在处理
  MAJOR_VIOLATION_RISK = 'major_violation_risk', // 重大违规风险
}

export enum RegulatoryApproval {
  ALL_APPROVALS_OBTAINED = 'all_approvals_obtained', // 已获得所有批文
  MOST_APPROVALS_OBTAINED = 'most_approvals_obtained', // 大部分批文已获得
  APPLYING_KEY_APPROVALS = 'applying_key_approvals', // 正在申请关键批文
  NOT_STARTED = 'not_started', // 尚未开始审批
}

export enum UrgencyLevel {
  VERY_URGENT = 'very_urgent', // 非常紧急（24小时内）
  URGENT = 'urgent', // 比较紧急（3天内）
  NORMAL = 'normal', // 一般紧急（1周内）
  NOT_URGENT = 'not_urgent', // 不急
}

// 主要问卷接口
export interface InitialScreeningQuestionnaire {
  _id?: string;
  id?: string;
  userId?: string;
  projectName?: string; // 项目名称

  // A. 发行主体资质核查
  issuerBasicInfo?: {
    issuerName?: string; // 发行人全称
    guarantorName?: string; // 担保人全称
    hasGuarantor?: boolean; // 是否有担保人
    enterpriseType?: EnterpriseType; // 企业性质
    registrationLocation?: RegistrationType; // 注册地
    registrationNumber?: string; // 注册号码
  };

  issuerRatingScale?: {
    creditRating?: CreditRating; // 主体信用评级
    internationalRating?: InternationalRating; // 国际评级
    totalAssets?: AssetScale; // 总资产规模
    industry?: Industry; // 所属行业
  };

  // B. 财务状况评估
  financialStatus?: {
    debtToAssetRatio?: DebtRatio; // 资产负债率
    netProfit?: ProfitScale; // 近一年净利润
    cashFlowStatus?: CashFlowStatus; // 现金流状况
    defaultRecord?: DefaultRecord; // 债务违约记录
  };

  // C. 绿色项目与发行计划
  greenProjectInfo?: {
    hasGreenProject?: GreenProjectStatus; // 是否有明确绿色项目
    projectCategories?: GreenProjectCategory[]; // 项目绿色属性类别
    greenStandardCompliance?: GreenStandardCompliance; // 是否符合绿色标准
    projectInvestmentScale?: InvestmentScale; // 项目总投资规模
  };

  issuancePlan?: {
    plannedIssuanceScale?: IssuanceScale; // 计划发行规模
    plannedIssuanceTime?: IssuanceTime; // 计划发行时间
    productType?: ProductType; // 发行产品类型
    targetMarket?: TargetMarket; // 目标发行市场
  };

  // D. 风险因素与联系信息
  riskFactors?: {
    majorLitigation?: LitigationStatus; // 重大诉讼或仲裁
    environmentalCompliance?: EnvironmentalCompliance; // 环保合规状况
    regulatoryApproval?: RegulatoryApproval; // 监管审批状态
  };

  contactInfo?: {
    contactName?: string; // 联系人姓名
    position?: string; // 职务
    phone?: string; // 联系电话
    email?: string; // 邮箱地址
    urgencyLevel?: UrgencyLevel; // 紧急程度
  };

  // 项目详细描述
  projectDescription?: string; // 项目详细描述（可选）

  // 补充说明
  additionalInfo?: string; // 其他需要说明的情况

  // 系统字段
  score?: number; // 自动评分
  aiScore?: number; // AI评分
  riskLevel?: 'low' | 'medium' | 'high'; // 风险等级
  decision?: ScreeningDecision; // 筛选决策
  status?: QuestionnaireStatus; // 问卷状态
  submittedAt?: string; // 提交时间
  reviewedAt?: string; // 审核时间
  reviewerId?: string; // 审核人ID
  reviewComments?: string; // 审核意见
  rejectionReasons?: string[]; // 拒绝原因列表

  // 渠道信息
  channelCode?: string; // 渠道代码
  channelName?: string; // 渠道名称

  createdAt?: string; // 创建时间
  updatedAt?: string; // 更新时间
}

// 表单验证规则
export interface QuestionnaireValidation {
  step1: boolean; // 发行主体资质核查
  step2: boolean; // 财务状况评估
  step3: boolean; // 绿色项目与发行计划
  step4: boolean; // 风险因素与联系信息
}

// 评分结果
export interface ScoringResult {
  totalScore: number;
  categoryScores: {
    subjectQualification: number; // 主体资质 35%
    financialStatus: number; // 财务状况 30%
    greenProject: number; // 绿色项目 25%
    riskFactors: number; // 风险因素 10%
  };
  decision: ScreeningDecision;
  recommendations: string[];
}

// API请求类型
export type QuestionnaireRequest = Omit<
  InitialScreeningQuestionnaire,
  | '_id'
  | 'createdAt'
  | 'updatedAt'
  | 'score'
  | 'aiScore'
  | 'decision'
  | 'reviewedAt'
  | 'reviewerId'
  | 'reviewComments'
>;

export interface ReviewDecisionRequest {
  questionnaireId: string;
  decision: ScreeningDecision;
  comments?: string;
  reasons?: string[];
}

export interface BatchOperationRequest {
  questionnaireIds: string[];
  action: 'approved' | 'rejected';
  comments?: string;
}

export interface QuestionnaireFilters {
  status?: QuestionnaireStatus[];
  industry?: string[];
  assetScale?: string[];
  creditRating?: string[];
  riskLevel?: ('low' | 'medium' | 'high')[];
  scoreRange?: [number, number] | string; // Bug修复: 支持数组或字符串格式
  dateRange?: [string, string] | null;
  search?: string;
  channel?: string[]; // 渠道筛选（前端UI用）
  channelId?: string; // 渠道ID筛选（后端API用）
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// API响应类型
export interface QuestionnaireApiResponse {
  success: boolean;
  data: {
    questionnaires: InitialScreeningQuestionnaire[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface QuestionnaireListResponse {
  questionnaires: InitialScreeningQuestionnaire[];
  total: number;
  page: number;
  limit: number;
}

export interface QuestionnaireDetailResponse {
  questionnaire: InitialScreeningQuestionnaire;
  scoringResult?: ScoringResult;
}

// 统计数据类型
export interface ScreeningStatistics {
  total_received: number;
  total_completed: number;
  pass_rate: number;
  average_processing_time: number;
  industry_distribution: Record<string, number>;
  monthly_trend: Array<{
    month: string;
    received: number;
    completed: number;
    pass_rate: number;
  }>;
  reviewer_workload: Array<{
    reviewer_id: string;
    reviewer_name: string;
    processed_count: number;
    avg_time: number;
  }>;
}

// 表单步骤定义
export interface QuestionnaireStep {
  key: string;
  title: string;
  description: string;
  questions: number;
  estimatedTime: string;
}

// 使用翻译函数获取问卷步骤
export const getQuestionnaireSteps = (
  t: (key: string) => string
): QuestionnaireStep[] => [
  {
    key: 'step1',
    title: t('questionnaire:steps.step1.title'),
    description: t('questionnaire:steps.step1.description'),
    questions: 9,
    estimatedTime: t('questionnaire:steps.step1.estimatedTime'),
  },
  {
    key: 'step2',
    title: t('questionnaire:steps.step2.title'),
    description: t('questionnaire:steps.step2.description'),
    questions: 4,
    estimatedTime: t('questionnaire:steps.step2.estimatedTime'),
  },
  {
    key: 'step3',
    title: t('questionnaire:steps.step3.title'),
    description: t('questionnaire:steps.step3.description'),
    questions: 8,
    estimatedTime: t('questionnaire:steps.step3.estimatedTime'),
  },
  {
    key: 'step4',
    title: t('questionnaire:steps.step4.title'),
    description: t('questionnaire:steps.step4.description'),
    questions: 8,
    estimatedTime: t('questionnaire:steps.step4.estimatedTime'),
  },
];

// 保留原常量供兼容（使用占位符键）
export const QUESTIONNAIRE_STEPS: QuestionnaireStep[] = [
  {
    key: 'step1',
    title: '发行主体资质核查',
    description: '企业基本信息、评级和规模',
    questions: 9,
    estimatedTime: '3分钟',
  },
  {
    key: 'step2',
    title: '财务状况评估',
    description: '关键财务指标和违约记录',
    questions: 4,
    estimatedTime: '2分钟',
  },
  {
    key: 'step3',
    title: '绿色项目与发行计划',
    description: '绿色项目情况和发行计划',
    questions: 8,
    estimatedTime: '3分钟',
  },
  {
    key: 'step4',
    title: '风险因素与联系信息',
    description: '风险评估和联系方式',
    questions: 8,
    estimatedTime: '2分钟',
  },
];

// 默认表单数据
export const DEFAULT_QUESTIONNAIRE: Partial<InitialScreeningQuestionnaire> = {
  projectName: '',
  issuerBasicInfo: {
    issuerName: '',
    guarantorName: '',
    hasGuarantor: false,
    enterpriseType: EnterpriseType.OTHER,
    registrationLocation: RegistrationType.MAINLAND_CHINA,
    registrationNumber: '',
  },
  issuerRatingScale: {
    creditRating: CreditRating.NO_RATING,
    internationalRating: InternationalRating.NO_INTERNATIONAL_RATING,
    totalAssets: AssetScale.BELOW_5B,
    industry: Industry.OTHER,
  },
  financialStatus: {
    debtToAssetRatio: DebtRatio.UNKNOWN,
    netProfit: ProfitScale.FROM_0_TO_100M,
    cashFlowStatus: CashFlowStatus.UNKNOWN,
    defaultRecord: DefaultRecord.NO_DEFAULT,
  },
  greenProjectInfo: {
    hasGreenProject: GreenProjectStatus.NO_CLEAR_PROJECT,
    projectCategories: [],
    greenStandardCompliance: GreenStandardCompliance.UNCERTAIN,
    projectInvestmentScale: InvestmentScale.BELOW_500M,
  },
  issuancePlan: {
    plannedIssuanceScale: IssuanceScale.BELOW_300M,
    plannedIssuanceTime: IssuanceTime.WITHIN_6_MONTHS,
    productType: ProductType.PRIVATE_GREEN_BOND,
    targetMarket: TargetMarket.FLEXIBLE,
  },
  riskFactors: {
    majorLitigation: LitigationStatus.NO_MAJOR_LITIGATION,
    environmentalCompliance: EnvironmentalCompliance.GOOD_COMPLIANCE,
    regulatoryApproval: RegulatoryApproval.NOT_STARTED,
  },
  contactInfo: {
    contactName: '',
    position: '',
    phone: '',
    email: '',
    urgencyLevel: UrgencyLevel.NORMAL,
  },
  status: QuestionnaireStatus.DRAFT,
};
